import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Grid,
  HStack,
  Skeleton,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";

type HistoryState = {
  items: any[];
  totalData: number;
  currentPage: number;
  totalPage: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string;
};

type HistoryTimelineProps = {
  title: string;
  emptyMessage: string;
  state: HistoryState;
  onLoadMore?: () => Promise<void> | void;
  eventLabelMap?: Record<string, string>;
  sourceLabelMap?: Record<string, string>;
  fieldLabelMap?: Record<string, string>;
  eventColorMap?: Record<string, string>;
  formatValue?: (field: string, value: any) => string;
};

const defaultEventColorMap: Record<string, string> = {
  created: "green",
  activated: "green",
  updated: "cyan",
  status_changed: "blue",
  payment_slip_attached: "blue",
  transaction_linked: "blue",
  payment_confirmed: "green",
  payment_failed: "red",
  payment_cancelled: "red",
  tadbir_submission_succeeded: "green",
  tadbir_submission_failed: "red",
  tadbir_status_synced: "cyan",
  sepah_submission_succeeded: "green",
  sepah_submission_failed: "red",
  deleted: "red",
  deactivated: "red",
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "—";

  const formatter = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Tehran",
  });

  const parts = Object.fromEntries(
    formatter
      .formatToParts(parsedDate)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );

  return `${parts.year}/${parts.month}/${parts.day} - ${parts.hour}:${parts.minute}`;
};

const formatInvestorLabel = (investor?: any) => {
  const fullName = String(investor?.fullName || "").trim();
  const nationalId = String(investor?.nationalId || "").trim();

  if (fullName && nationalId) {
    return `${fullName} - ${nationalId}`;
  }

  return fullName || nationalId || "—";
};

const defaultFormatValue = (field: string, value: any) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (
    field.toLowerCase().includes("at") ||
    field.toLowerCase().includes("date")
  ) {
    return formatDateTime(value);
  }

  if (typeof value === "boolean") {
    return value ? "بله" : "خیر";
  }

  if (typeof value === "number") {
    return Number(value).toLocaleString("fa-IR");
  }

  if (Array.isArray(value)) {
    return value.join("، ");
  }

  if (typeof value === "object") {
    if (value?.fullName || value?.nationalId) {
      return formatInvestorLabel(value);
    }

    if (typeof value?._id === "string") {
      return value._id;
    }

    try {
      return JSON.stringify(value);
    } catch {
      return "—";
    }
  }

  return String(value);
};

const isStructuredValue = (value: any) =>
  value !== null &&
  value !== undefined &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  !(value?.fullName || value?.nationalId);

const stringifyStructuredValue = (value: any) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value ?? "—");
  }
};

const normalizeFieldLabel = (
  field: string,
  fieldLabelMap: Record<string, string>,
) => {
  if (fieldLabelMap[field]) {
    return fieldLabelMap[field];
  }

  return String(field || "")
    .split(".")
    .filter(Boolean)
    .map((part) =>
      part
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .trim(),
    )
    .join(" / ");
};

const buildChangePreview = (field: string, value: any) => {
  const fromValue = defaultFormatValue(field, value?.from);
  const toValue = defaultFormatValue(field, value?.to);

  return {
    fromValue,
    toValue,
    hasRealChange: fromValue !== toValue,
  };
};

const getEventLabel = (item: any, eventLabelMap: Record<string, string>) =>
  eventLabelMap[String(item?.eventType || "").trim()] ||
  item?.eventType ||
  "تغییر";

const getSourceLabel = (item: any, sourceLabelMap: Record<string, string>) =>
  sourceLabelMap[String(item?.source || "").trim()] || item?.source || "سیستم";

const getEventColorScheme = (
  item: any,
  eventColorMap: Record<string, string>,
) =>
  eventColorMap[String(item?.eventType || "").trim()] || "gray";

const buildActorLabel = (item: any, sourceLabelMap: Record<string, string>) => {
  if (item?.actorType === "user") {
    const actorLabel = formatInvestorLabel(item?.actorUser);
    return actorLabel !== "—" ? `توسط ${actorLabel}` : "توسط کاربر";
  }

  return `توسط ${getSourceLabel(item, sourceLabelMap)}`;
};

type ChangedFieldValue = {
  from?: any;
  to?: any;
};

const renderChangeSummary = ({
  item,
  borderColor,
  colorScheme,
  surfaceBg,
  primaryTextColor,
  secondaryTextColor,
  fieldLabelMap,
  formatValue,
  onCopyValue,
}: {
  item: any;
  borderColor: string;
  colorScheme: string;
  surfaceBg: string;
  primaryTextColor: string;
  secondaryTextColor: string;
  fieldLabelMap: Record<string, string>;
  formatValue: (field: string, value: any) => string;
  onCopyValue: (value: string) => Promise<void> | void;
}) => {
  const changedFields = item?.changedFields || {};
  const entries = Object.entries(changedFields)
    .map(([field, value]) => {
      const normalizedValue = (value || {}) as ChangedFieldValue;

      return {
        field,
        value: normalizedValue,
        label: normalizeFieldLabel(field, fieldLabelMap),
        preview: buildChangePreview(field, normalizedValue),
      };
    })
    .filter((entry) => entry.preview.hasRealChange);

  if (!entries.length) {
    return null;
  }

  return (
    <Accordion allowToggle reduceMotion>
      <AccordionItem
        border="1px solid"
        borderColor={borderColor}
        borderRadius="xl"
        bg={surfaceBg}
        overflow="hidden"
      >
        <AccordionButton px={3} py={3} _hover={{ bg: "transparent" }}>
          <HStack flex="1" justify="space-between" align="center" spacing={3}>
            <Text fontSize="sm" fontWeight="bold" color={primaryTextColor}>
              جزئیات تغییرات
            </Text>
            <Badge colorScheme={colorScheme} borderRadius="full" px={3} py={1}>
              {entries.length.toLocaleString("fa-IR")} تغییر
            </Badge>
          </HStack>
          <AccordionIcon color={secondaryTextColor} />
        </AccordionButton>

        <AccordionPanel px={3} pb={3} pt={0}>
          <VStack align="stretch" spacing={2}>
            {entries.map((entry) => (
              <Box
                key={entry.field}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="xl"
                px={3}
                py={3}
                bg="transparent"
                overflow="hidden"
              >
                <VStack align="stretch" spacing={3} minW={0}>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color={primaryTextColor}
                    wordBreak="break-word"
                  >
                    {entry.label}
                  </Text>

                  <Grid templateColumns="1fr" gap={3} minW={0}>
                    <Box
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="lg"
                      px={3}
                      py={2}
                      bg="transparent"
                      minW={0}
                    >
                      <Text fontSize="xs" color={secondaryTextColor} mb={1}>
                        مقدار قبلی
                      </Text>
                      {isStructuredValue(entry.value?.from) ? (
                        <VStack align="stretch" spacing={2}>
                          <Button
                            size="xs"
                            alignSelf="flex-start"
                            variant="outline"
                            onClick={() =>
                              onCopyValue(
                                stringifyStructuredValue(entry.value?.from),
                              )
                            }
                          >
                            کپی JSON
                          </Button>
                          <Textarea
                            value={stringifyStructuredValue(entry.value?.from)}
                            readOnly
                            size="sm"
                            minH="140px"
                            fontFamily="mono"
                            whiteSpace="pre"
                            dir="ltr"
                            textAlign="left"
                          />
                        </VStack>
                      ) : (
                        <Text
                          fontSize="sm"
                          color={primaryTextColor}
                          wordBreak="break-word"
                          overflowWrap="anywhere"
                          whiteSpace="pre-wrap"
                        >
                          {formatValue(entry.field, entry.value?.from)}
                        </Text>
                      )}
                    </Box>

                    <Box
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="lg"
                      px={3}
                      py={2}
                      bg="transparent"
                      minW={0}
                    >
                      <Text fontSize="xs" color={secondaryTextColor} mb={1}>
                        مقدار جدید
                      </Text>
                      {isStructuredValue(entry.value?.to) ? (
                        <VStack align="stretch" spacing={2}>
                          <Button
                            size="xs"
                            alignSelf="flex-start"
                            variant="outline"
                            onClick={() =>
                              onCopyValue(
                                stringifyStructuredValue(entry.value?.to),
                              )
                            }
                          >
                            کپی JSON
                          </Button>
                          <Textarea
                            value={stringifyStructuredValue(entry.value?.to)}
                            readOnly
                            size="sm"
                            minH="140px"
                            fontFamily="mono"
                            whiteSpace="pre"
                            dir="ltr"
                            textAlign="left"
                          />
                        </VStack>
                      ) : (
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color={primaryTextColor}
                          wordBreak="break-word"
                          overflowWrap="anywhere"
                          whiteSpace="pre-wrap"
                        >
                          {formatValue(entry.field, entry.value?.to)}
                        </Text>
                      )}
                    </Box>
                  </Grid>
                </VStack>
              </Box>
            ))}
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export const HistoryTimeline: React.FC<HistoryTimelineProps> = ({
  title,
  emptyMessage,
  state,
  onLoadMore,
  eventLabelMap = {},
  sourceLabelMap = {},
  fieldLabelMap = {},
  eventColorMap = defaultEventColorMap,
  formatValue = defaultFormatValue,
}) => {
  const toast = useToast();
  const timelineAccent = useColorModeValue("brand.500", "brand.300");
  const historySurfaceBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const historyMutedBg = useColorModeValue("white", "whiteAlpha.50");
  const historyInsetBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const historyTextColor = useColorModeValue("gray.800", "gray.100");
  const historySecondaryTextColor = useColorModeValue("gray.600", "gray.300");
  const historyBorderColor = useColorModeValue(
    "blackAlpha.100",
    "whiteAlpha.200",
  );

  const historyItems = state.items || [];
  const handleCopyValue = React.useCallback(
    async (value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        toast({
          title: "JSON کپی شد",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } catch {
        toast({
          title: "کپی JSON ناموفق بود",
          status: "error",
          duration: 2500,
          isClosable: true,
        });
      }
    },
    [toast],
  );

  return (
    <Box
      borderWidth="1px"
      borderColor={historyBorderColor}
      borderRadius="2xl"
      p={4}
      bg={historySurfaceBg}
    >
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between" align="center">
          <Text fontWeight="bold">{title}</Text>
          <Badge colorScheme="brand" variant="subtle">
            {Number(state.totalData || historyItems.length).toLocaleString("fa-IR")}{" "}
            رویداد
          </Badge>
        </HStack>

        {state.isLoading ? (
          <VStack align="stretch" spacing={3}>
            <Skeleton height="86px" borderRadius="xl" />
            <Skeleton height="86px" borderRadius="xl" />
            <Skeleton height="86px" borderRadius="xl" />
          </VStack>
        ) : null}

        {!state.isLoading && state.error ? (
          <Text fontSize="sm" color="red.400">
            {state.error}
          </Text>
        ) : null}

        {!state.isLoading && !state.error && historyItems.length === 0 ? (
          <Text fontSize="sm" color={historySecondaryTextColor}>
            {emptyMessage}
          </Text>
        ) : null}

        {!state.isLoading && !state.error && historyItems.length > 0 ? (
          <VStack align="stretch" spacing={0} position="relative">
            {historyItems.map((item, index) => {
              const colorScheme = getEventColorScheme(item, eventColorMap);
              const isLastItem = index === historyItems.length - 1;

              return (
                <HStack
                  key={String(item?._id || `${item?.eventType || "history"}-${index}`)}
                  align="stretch"
                  spacing={4}
                  position="relative"
                >
                  <VStack align="center" spacing={0} minW="32px" pt={1}>
                    <Box
                      boxSize="14px"
                      borderRadius="full"
                      bg={`${colorScheme}.500`}
                      borderWidth="4px"
                      borderColor={`${colorScheme}.100`}
                      zIndex={1}
                    />
                    {!isLastItem ? (
                      <Box
                        flex="1"
                        width="2px"
                        bg={timelineAccent}
                        opacity={0.28}
                        minH="32px"
                      />
                    ) : (
                      <Box flex="1" minH="12px" />
                    )}
                  </VStack>

                  <Box flex="1" pb={isLastItem ? 0 : 5}>
                    <Box
                      borderWidth="1px"
                      borderColor={historyBorderColor}
                      borderRadius="2xl"
                      px={4}
                      py={4}
                      bg={historyMutedBg}
                      boxShadow="sm"
                    >
                      <VStack align="stretch" spacing={3}>
                        <HStack
                          justify="space-between"
                          align="flex-start"
                          flexWrap="wrap"
                          spacing={3}
                        >
                          <VStack align="stretch" spacing={2} flex="1" minW={0}>
                            <HStack spacing={2} flexWrap="wrap">
                              <Badge colorScheme={colorScheme} borderRadius="full" px={3} py={1}>
                                {getEventLabel(item, eventLabelMap)}
                              </Badge>
                              <Badge
                                variant="outline"
                                colorScheme="gray"
                                borderRadius="full"
                                px={3}
                                py={1}
                              >
                                {getSourceLabel(item, sourceLabelMap)}
                              </Badge>
                            </HStack>
                            <Text fontSize="sm" color={historySecondaryTextColor}>
                              {buildActorLabel(item, sourceLabelMap)}
                            </Text>
                          </VStack>

                          <Box
                            borderRadius="xl"
                            px={3}
                            py={2}
                            minW={{ base: "100%", sm: "auto" }}
                          >
                            <Text fontSize="xs" color={historySecondaryTextColor} mb={1}>
                              زمان ثبت
                            </Text>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color={historyTextColor}
                            >
                              {formatDateTime(item?.createdAt)}
                            </Text>
                          </Box>
                        </HStack>

                        {renderChangeSummary({
                          item,
                          borderColor: historyBorderColor,
                          colorScheme,
                          surfaceBg: historyInsetBg,
                          primaryTextColor: historyTextColor,
                          secondaryTextColor: historySecondaryTextColor,
                          fieldLabelMap,
                          formatValue,
                          onCopyValue: handleCopyValue,
                        })}

                        {item?.notes ? (
                          <Box
                            borderWidth="1px"
                            borderColor={historyBorderColor}
                            borderRadius="xl"
                            bg={historyInsetBg}
                            px={3}
                            py={2}
                          >
                            <Text fontSize="sm" color={historySecondaryTextColor}>
                              {item.notes}
                            </Text>
                          </Box>
                        ) : null}
                      </VStack>
                    </Box>
                  </Box>
                </HStack>
              );
            })}

            {onLoadMore && state.currentPage < state.totalPage ? (
              <Button
                onClick={onLoadMore}
                isLoading={state.isLoadingMore}
                variant="outline"
                alignSelf="center"
              >
                نمایش رویدادهای بیشتر
              </Button>
            ) : null}
          </VStack>
        ) : null}
      </VStack>
    </Box>
  );
};
